import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Button, Alert, Tabs, Tab, Spinner } from 'react-bootstrap';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './BookingManagement.css';
import { zhTW } from 'date-fns/locale';

function BookingManagement() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [processingBookingId, setProcessingBookingId] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [tempBookingStatuses, setTempBookingStatuses] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);

  //console.log('BookingManagement');
  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bookings/');
      // 排序
      const sortedBookings = response.data.sort((a, b) => {
        const dateComparison = new Date(a.date) - new Date(b.date);
        if (dateComparison !== 0) return dateComparison;
        return a.time_slot === 'MORNING' ? -1 : 1;
      });
      setBookings(sortedBookings);
    } catch (err) {
      setError('無法獲取預約記錄');
      //toast.error('無法獲取預約記錄，請稍後再試');
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (bookingId) => {
    try {
      setProcessingBookingId(bookingId);
      const booking = bookings.find(b => b.id === bookingId);
      
      await api.post(`/bookings/${bookingId}/accept/`);
      await fetchBookings();
      
      toast.success(
        <div>
          已接受預約
          <br />
          學員：{booking.student_name}
          <br />
          日期：{format(new Date(booking.date), 'yyyy/MM/dd')}
          <br />
          時段：{booking.time_slot_display}
        </div>,
        {
          position: "top-right",
          autoClose: 5000
        }
      );
    } catch (err) {
      toast.error('接受預約失敗，請稍後再試');
    } finally {
      setProcessingBookingId(null);
    }
  };

  const handleDeny = async (bookingId) => {
    try {
      setProcessingBookingId(bookingId);
      const booking = bookings.find(b => b.id === bookingId);
      
      await api.post(`/bookings/${bookingId}/deny/`);
      await fetchBookings();
      
      toast.info(
        <div>
          已拒絕預約
          <br />
          學員：{booking.student_name}
          <br />
          日期：{format(new Date(booking.date), 'yyyy/MM/dd')}
          <br />
          時段：{booking.time_slot_display}
        </div>,
        {
          position: "top-right",
          autoClose: 5000
        }
      );
    } catch (err) {
      toast.error('拒絕預約失敗，請稍後再試');
    } finally {
      setProcessingBookingId(null);
    }
  };

  const handleAcceptAll = (bookingNo) => {
    const bookingsToAccept = bookings.filter(
      b => b.booking_no === bookingNo && b.status === 'PENDING'
    );
    
    // 更新所有相關預約的臨時狀態
    const newTempStatuses = {};
    bookingsToAccept.forEach(booking => {
      newTempStatuses[booking.id] = 'ACCEPTED';
    });
    
    setTempBookingStatuses(prev => ({
      ...prev,
      ...newTempStatuses
    }));
  };

  const denyAll = (bookingNo) => {
    const bookingsToDeny = bookings.filter(
      b => b.booking_no === bookingNo && b.status === 'PENDING'
    );
    
    // 更新所有相關預約的臨時狀態
    const newTempStatuses = {};
    bookingsToDeny.forEach(booking => {
      newTempStatuses[booking.id] = 'DENIED';
    });
    
    setTempBookingStatuses(prev => ({
      ...prev,
      ...newTempStatuses
    }));
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'ACCEPTED':
        return 'success';
      case 'DENIED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'PENDING':
        return '待處理';
      case 'ACCEPTED':
        return '已接受';
      case 'DENIED':
        return '已拒絕';
      default:
        return status;
    }
  };

  // 格式化日期的輔助函數
  const formatDate = (dateString) => {
    return format(new Date(dateString), 'yyyy/MM/dd', { locale: zhTW });
  };

  const formatDateWithDaysAgo = (dateString) => {
    const date = new Date(dateString);
    const formattedDate = format(date, 'yyyy/MM/dd', { locale: zhTW });
    const daysAgo = formatDistanceToNow(date, { locale: zhTW, addSuffix: true });
    return (
      <span style={{ backgroundColor: 'lightgray', padding: '8px 8px', borderRadius: '4px' }}>
        {daysAgo}的預約
      </span>
    );
  };

  const getCourseTypeDisplay = (type) => {
    return type==="SKI"?"雙板":"單板";
  };

  const handleTempStatusChange = (bookingId, newStatus) => {
    setTempBookingStatuses(prev => ({
      ...prev,
      [bookingId]: newStatus
    }));
  };

  const handleConfirmChanges = async (bookingNo) => {
    const bookingsToUpdate = bookings.filter(b => b.booking_no === bookingNo);
    const changes = bookingsToUpdate.map(booking => ({
      id: booking.id,
      status: tempBookingStatuses[booking.id] || booking.status
    }));

    try {
      // 發送批量更新請求到後端
      await api.post('/bookings/bulk_update/', {
        bookings: changes
      });
      
      await fetchBookings();
      setTempBookingStatuses({});
      setShowConfirmModal(false);
      
      toast.success('預約狀態更新成功');
    } catch (err) {
      toast.error('更新預約狀態失敗，請稍後再試');
    }
  };

  const renderPendingTableHeader = (bookingNo, groupBookings) => (
    isMobileView ? (
      <thead>
        <tr className="table-primary">
          <th colSpan="10">
            <div className="d-flex flex-column">
              <div className="mb-2">
                <span>預約編號: {bookingNo}</span>
              </div>
              <div className="d-flex">
                <Button 
                  variant="success"
                  size="sm"
                  className="action-button me-3"
                  onClick={() => handleAcceptAll(bookingNo)}
                >
                  全部接受
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="action-button"
                  onClick={() => denyAll(bookingNo)}
                >
                  全部拒絕
                </Button>
              </div>
            </div>
          </th>
        </tr>
        <tr>
          <th colSpan="10">
            <div className="d-flex flex-column">
              <div>學員姓名: {groupBookings[0].student_name}</div>
              <div className="d-flex justify-content-between mt-1">
                <span>人數: {groupBookings[0].numberof_students}</span>
                <span>{formatDateWithDaysAgo(groupBookings[0].created_at)}</span>
              </div>
              <div className="mt-1">期望地點: {groupBookings[0].preferred_location}</div>
            </div>
          </th>
        </tr>
        {groupBookings[0].note && (
          <tr>
            <th colSpan="10">備註: {groupBookings[0].note}</th>
          </tr>
        )}
        <tr>
          <th colSpan="3">課程日期</th>
          <th colSpan="2">時段</th>
          <th colSpan="2">類型</th>
          <th colSpan="3">操作</th>
        </tr>
      </thead>
    ) : (
      <thead>
        <tr className="table-primary">
          <th colSpan="8">預約編號: {bookingNo}</th>
          <th colSpan="2">
            <div className="d-flex justify-content-center align-items-center button-container">
              <Button
                variant="success"
                size="sm"
                className="action-button me-4"
                onClick={() => handleAcceptAll(bookingNo)}
              >
                全部接受
              </Button>
              <Button
                variant="danger"
                size="sm"
                className="action-button"
                onClick={() => denyAll(bookingNo)}
              >
                全部拒絕
              </Button>
            </div>
          </th>
        </tr>
        {groupBookings[0].note && (
          <tr>
            <th colSpan="10">備註: {groupBookings[0].note}</th>
          </tr>
        )}
        <tr>
          <th colSpan="4">學員姓名: {groupBookings[0].student_name}</th>
          <th colSpan="1">人數: {groupBookings[0].numberof_students}</th>
          <th colSpan="3">期望地點: {groupBookings[0].preferred_location}</th>
          <th colSpan="2">{formatDateWithDaysAgo(groupBookings[0].created_at)}</th>
        </tr>
        <tr>
          <th colSpan="4">課程日期</th>
          <th colSpan="1">時段</th>
          <th colSpan="1">課程類型</th>
          <th colSpan="2">狀態</th>
          <th colSpan="2">操作</th>
        </tr>
      </thead>
    )
  );

  const renderOtherTableHeader = (bookingNo, groupBookings) => {
    return isMobileView ? (
    <thead>
      <tr className="table-primary">
        <th colSpan="10">預約編號: {bookingNo}</th>
      </tr>
      <tr>
        <th colSpan="10">申請日期: {formatDate(groupBookings[0].created_at)}</th>
      </tr>
      <tr>
        <th colSpan="6">學員姓名: {groupBookings[0].student_name}</th>
        <th colSpan="3">期望地點: {groupBookings[0].preferred_location}</th>
        <th colSpan="1">人數: {groupBookings[0].numberof_students}</th>
      </tr>
      {groupBookings[0].note && (
        <tr>
          <th colSpan="10">備註: {groupBookings[0].note}</th>
        </tr>
      )}
      <tr>
        <th colSpan="3">課程日期</th>
        <th colSpan="3">時段</th>
        <th colSpan="2">課程類型</th>
        <th colSpan="2">狀態</th>
      </tr>
    </thead>
  ) : (
    <thead>
      <tr className="table-primary">
        <th colSpan="10">預約編號: {bookingNo}</th>
      </tr>
      <tr>
        <th colSpan="3">學員姓名: {groupBookings[0].student_name}</th>
        <th colSpan="2">人數: {groupBookings[0].numberof_students}</th>
        <th colSpan="3">期望地點: {groupBookings[0].preferred_location}</th>
        <th colSpan="2">申請日期: {formatDate(groupBookings[0].created_at)}</th>
      </tr>
      {groupBookings[0].note && (
        <tr>
          <th colSpan="10">備註: {groupBookings[0].note}</th>
        </tr>
      )}
      <tr>
        <th colSpan="3">課程日期</th>
        <th colSpan="3">時段</th>
        <th colSpan="2">課程類型</th>
        <th colSpan="2">狀態</th>
      </tr>
    </thead>
    )
  };

  const renderPendingTableBody = (groupBookings) => {
    const allDecided = groupBookings.every(
      (booking) => tempBookingStatuses[booking.id] || booking.status !== 'PENDING'
    );

    return isMobileView ? (
      <tbody>
        {groupBookings.map((booking) => (
          <tr key={booking.id}>
            <td colSpan="3">{formatDate(booking.date)}</td>
            <td colSpan="2">{booking.time_slot_display}</td>
            <td colSpan="2">{getCourseTypeDisplay(booking.type)}</td>
            <td colSpan="3" className="action-cell">
              {booking.status === 'PENDING' && (
                <div className="d-flex justify-content-center align-items-center button-container">
                  <Button
                    variant={tempBookingStatuses[booking.id] === 'ACCEPTED' ? 'success' : 'outline-success'}
                    size="sm"
                    className="me-2"
                    style={{ minWidth: '60px' }}
                    onClick={() => handleTempStatusChange(booking.id, 'ACCEPTED')}
                  >
                    接受
                  </Button>
                  <Button
                    variant={tempBookingStatuses[booking.id] === 'DENIED' ? 'danger' : 'outline-danger'}
                    size="sm"
                    style={{ minWidth: '60px' }}
                    onClick={() => handleTempStatusChange(booking.id, 'DENIED')}
                  >
                    拒絕
                  </Button>
                </div>
              )}
            </td>
          </tr>
        ))}
        {Object.keys(tempBookingStatuses).length > 0 && (
          <tr>
            <td colSpan="10" className="text-center">
              <Button
                variant="primary"
                onClick={() => handleConfirmChanges(groupBookings[0].booking_no)}
                className="me-4"
                disabled={!allDecided}
              >
                確認預約結果
              </Button>
              <Button
                variant="secondary"
                onClick={() => setTempBookingStatuses({})}
              >
                取消所有變更
              </Button>
              {!allDecided && (
                <div className="text-danger mt-2">
                  請確保所有預約都已決定狀態
                </div>
              )}
            </td>
          </tr>
        )}
      </tbody>
    ) : (
      <tbody>
        {groupBookings.map((booking) => (
          <tr key={booking.id}>
            <td colSpan="4">{formatDate(booking.date)}</td>
            <td colSpan="1">{booking.time_slot_display}</td>
            <td colSpan="1">{booking.type_display}</td>
            <td colSpan="2">
              <Badge bg={getStatusBadgeVariant(tempBookingStatuses[booking.id] || booking.status)}>
                {getStatusDisplay(tempBookingStatuses[booking.id] || booking.status)}
              </Badge>
            </td>
            <td colSpan="2" className="action-cell">
              {booking.status === 'PENDING' && (
                <div className="d-flex justify-content-center align-items-center button-container">
                  <Button
                    variant={tempBookingStatuses[booking.id] === 'ACCEPTED' ? 'success' : 'outline-success'}
                    size="sm"
                    className="action-button me-4"
                    onClick={() => handleTempStatusChange(booking.id, 'ACCEPTED')}
                  >
                    接受
                  </Button>
                  <Button
                    variant={tempBookingStatuses[booking.id] === 'DENIED' ? 'danger' : 'outline-danger'}
                    size="sm"
                    className="action-button"
                    onClick={() => handleTempStatusChange(booking.id, 'DENIED')}
                  >
                    拒絕
                  </Button>
                </div>
              )}
            </td>
          </tr>
        ))}
        {Object.keys(tempBookingStatuses).length > 0 && (
          <tr>
            <td colSpan="10" className="text-center">
              <Button
                variant="primary"
                onClick={() => handleConfirmChanges(groupBookings[0].booking_no)}
                className="me-4"
                disabled={!allDecided}
              >
                確認預約結果
              </Button>
              <Button
                variant="secondary"
                onClick={() => setTempBookingStatuses({})}
              >
                取消所有變更
              </Button>
              {!allDecided && (
                <div className="text-danger mt-2">
                  請確保所有預約都已決定狀態
                </div>
              )}
            </td>
          </tr>
        )}
      </tbody>
    );
  };

  const renderOtherTableBody = (groupBookings) => (
    isMobileView ? (
      <tbody>
      {groupBookings.map((booking) => (
        <tr key={booking.id}>
          <td colSpan="4">{formatDate(booking.date)}</td>
          <td colSpan="2">{booking.time_slot_display}</td>
          <td colSpan="3">{booking.type_display}</td>
          <td colSpan="1">
            <Badge bg={getStatusBadgeVariant(booking.status)}>
              {booking.status_display}
            </Badge>
          </td>
        </tr>
      ))}
      </tbody>
    ):(
    <tbody>
      {groupBookings.map((booking) => (
        <tr key={booking.id}>
          <td colSpan="3">{formatDate(booking.date)}</td>
          <td colSpan="2">{booking.time_slot_display}</td>
          <td colSpan="3">{booking.type_display}</td>
          <td colSpan="2">
            <Badge bg={getStatusBadgeVariant(booking.status)}>
              {booking.status_display}
            </Badge>
          </td>
        </tr>
      ))}
      </tbody>
    )
  );

  const renderBookingTable = (bookings) => {

    // 按預約編號分組
    const groupedBookings = bookings.reduce((acc, booking) => {
      if (!acc[booking.booking_no]) {
        acc[booking.booking_no] = [];
      }
      acc[booking.booking_no].push(booking);
      return acc;
    }, {});

    return (
      <div className="booking-groups">
        {Object.entries(groupedBookings).map(([bookingNo, groupBookings]) => (
          <div key={bookingNo} className="booking-group mb-4">
            <Table striped bordered hover responsive>
              {activeTab === 'pending' 
                ? renderPendingTableHeader(bookingNo, groupBookings)
                : renderOtherTableHeader(bookingNo, groupBookings)
              }
              {activeTab === 'pending'
                ? renderPendingTableBody(groupBookings)
                : renderOtherTableBody(groupBookings)
              }
            </Table>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div>載入中...</div>;

  const pendingBookings = bookings.filter(booking => booking.status === 'PENDING');
  const otherBookings = bookings.filter(booking => booking.status !== 'PENDING');
  const acceptedBookings = bookings.filter(booking => booking.status === 'ACCEPTED');

  return (
    <Container className="py-4">
      <h2 className="mb-4">預約管理</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">載入中...</span>
          </Spinner>
        </div>
      ) : (
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab 
            eventKey="pending" 
            title={
              <>
                待處理預約
                {pendingBookings.length > 0 && (
                  <Badge bg="danger" className="ms-2">
                    {pendingBookings.length}
                  </Badge>
                )}
              </>
            }
          >
            {pendingBookings.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">目前沒有待處理的預約</p>
              </div>
            ) : (
              renderBookingTable(pendingBookings)
            )}
          </Tab>
          <Tab eventKey="others" title="已處理預約">
            {otherBookings.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">目前沒有其他預約記錄</p>
              </div>
            ) : (
              renderBookingTable(otherBookings)
            )}
          </Tab>
          <Tab eventKey="accepted" title="已接受預約">
            {acceptedBookings.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">目前沒有已接受的預約</p>
              </div>
            ) : (
              renderBookingTable(acceptedBookings)
            )}
          </Tab>
        </Tabs>
      )}
    </Container>
  );
}

export default BookingManagement; 